import React from 'react'
import { AlertTriangle, CheckCircle2, ExternalLink, Loader2, UploadCloud, XCircle } from 'lucide-react'
import { mergeClasses } from '../../helpers/styling'
import { SvgIcon, SvgIconType } from '../svg-icon'
import { FloatingPanel } from '../ui-primitives/floating-panel'
import { Button } from '../ui-primitives/button'
import { Checkbox } from '../ui-primitives/checkbox'
import { RiskStatus } from '../widgets/CodeWidget/analysis/code-widget-analysis'
import { buildFixPrompt } from '../widgets/CodeWidget/analysis/code-widget-fix-prompt'
import { CHECK_TIME_LIMIT_MS } from '../widgets/CodeWidget/analysis/use-code-widget-check'
import { PublishCheck, WidgetPublishCheck } from './publish-check'

export const LOCK_AND_PUBLISH_GUIDELINES_URL =
  'https://bim-connected.slite.com/app/docs/-1nAHhv2p9KvWC/Lock-and-Publish-guidelines'

/** `checking` is the first scan, before there is any colour to show. */
type Tone = RiskStatus | 'checking'

const STATUS_STYLE: Record<Tone, { icon: SvgIconType; iconColor: string; banner: string; dot: string; label: string }> = {
  flagged: {
    icon: XCircle,
    iconColor: 'text-red-600',
    banner: 'bg-red-50 text-red-800',
    dot: 'bg-red-500',
    label: 'Fix before publishing',
  },
  review: {
    icon: AlertTriangle,
    iconColor: 'text-amber-600',
    banner: 'bg-amber-50 text-amber-800',
    dot: 'bg-amber-500',
    label: 'Needs a look',
  },
  clear: {
    icon: CheckCircle2,
    iconColor: 'text-emerald-600',
    banner: 'bg-emerald-50 text-emerald-800',
    dot: 'bg-emerald-500',
    label: 'Clear',
  },
  checking: {
    icon: Loader2,
    iconColor: 'animate-spin text-muted-foreground',
    banner: 'bg-muted text-muted-foreground',
    dot: 'bg-muted-foreground/40',
    label: 'Checking',
  },
}

/** Grey before the first scan finishes; a running scan keeps the last colour and pulses. */
export function PublishStatusDot(props: { check: PublishCheck }) {
  const { status, progress } = props.check
  const label =
    progress === 'timed-out'
      ? 'Check did not finish'
      : progress === 'failed'
      ? 'Check could not run'
      : STATUS_STYLE[status ?? 'checking'].label
  return (
    <span
      role="img"
      aria-label={`Security check: ${label}`}
      className={mergeClasses('ml-1.5 inline-block h-2.5 w-2.5 shrink-0 rounded-full', STATUS_STYLE[status ?? 'checking'].dot, {
        'animate-pulse': progress === 'checking',
      })}
    />
  )
}

export function LockAndPublishCard(props: { check: PublishCheck; inspectorGadgetIsEnabled: boolean; onClose: () => void }) {
  const [includeHtml, setIncludeHtml] = React.useState(false)
  const needsWork = props.check.widgets.filter((entry) => entry.status !== 'clear')
  const style = STATUS_STYLE[props.check.status ?? 'checking']
  const rechecking = props.check.progress === 'checking' && props.check.status !== null

  return (
    <FloatingPanel
      title="Lock&Publish"
      icon={UploadCloud}
      onClose={props.onClose}
      defaultSize={{ width: 620, height: 560 }}
      minSize={{ width: 420, height: 320 }}
    >
      <div className="flex h-full flex-col gap-3 overflow-auto p-4 text-sm text-foreground">
        <header>
          <h2 className="m-0 text-base font-semibold">Code Widget security pre-check</h2>
          <p className="m-0 mt-1 text-muted-foreground">
            Runs while you configure the app: it reads every Code Widget and lists what could put the people using
            the app, or its data, at risk. Green means this check found nothing it looks for — not that the app is
            safe or ready for Lock&Publish.
          </p>
        </header>

        <div className={mergeClasses('flex items-start gap-2 rounded-md px-3 py-2', style.banner)}>
          <SvgIcon icon={style.icon} size={18} className={mergeClasses('mt-0.5 shrink-0', style.iconColor)} />
          <span className="font-medium">{summaryOf(props.check, props.inspectorGadgetIsEnabled)}</span>
        </div>
        {rechecking && (
          <p className="m-0 flex items-center gap-1.5 text-xs text-muted-foreground">
            <SvgIcon icon={Loader2} size={12} className="animate-spin" />
            Checking the latest changes — showing the previous result.
          </p>
        )}

        {needsWork.length > 0 && (
          <>
            <p className="text-muted-foreground">
              Code Widget pages are usually written by a coding agent. Copy a fix prompt into the conversation that
              produced the page, paste the page it returns into the widget, and this check updates.
            </p>
            {/*
              * A sibling label, not a wrapping one: the checkbox is a button, and a label around it re-clicks
              * it after its own click, so every toggle undid itself.
              */}
            <div className="flex items-center gap-2">
              <Checkbox
                id="fix-prompt-include-html"
                checked={includeHtml}
                onCheckedChange={(checked) => setIncludeHtml(checked === true)}
              />
              <label htmlFor="fix-prompt-include-html" className="m-0 cursor-pointer font-normal">
                Include the page in the prompt
              </label>
            </div>
            <ul className="space-y-2">
              {needsWork.map((entry) => (
                <WidgetRow key={entry.widget.paths[0]} entry={entry} includeHtml={includeHtml} />
              ))}
            </ul>
          </>
        )}

        <p className="rounded-md border border-border p-3 text-xs leading-relaxed text-muted-foreground">
          <span className="font-semibold text-foreground">Disclaimer. </span>
          This check reads the source of each Code Widget and lists what it can see; it does not certify the app as
          safe. The configurators who build and change this app — developers included, when they configure — are
          responsible for what its Code Widgets do: the queries they run, the data they change, and what they show on
          screen, including to anyone who can see that screen. That responsibility stays with them when the app is
          published, whoever carries out the publishing. Wistor provides the platform; what an app does with it is
          decided by its configuration.
        </p>

        <div>
          <Button variant="outline" size="sm" onClick={() => window.open(LOCK_AND_PUBLISH_GUIDELINES_URL)}>
            Open Lock&Publish guidelines <SvgIcon icon={ExternalLink} size={14} className="ml-1" />
          </Button>
        </div>
      </div>
    </FloatingPanel>
  )
}

function WidgetRow(props: { entry: WidgetPublishCheck; includeHtml: boolean }) {
  const { entry } = props
  const style = STATUS_STYLE[entry.status]

  return (
    <li className="rounded-md border border-border bg-card p-3">
      <div className="flex items-start gap-2">
        <SvgIcon icon={style.icon} size={16} className={mergeClasses('mt-0.5 shrink-0', style.iconColor)} />
        <div className="min-w-0 flex-1">
          <div className="font-medium">{entry.label}</div>
          <ul className="mt-1 space-y-0.5 text-muted-foreground">
            {entry.findings.map((finding) => (
              <li key={finding.id}>
                {finding.title}
                {finding.evidence.length > 0 && (
                  <span className="text-xs"> — line {finding.evidence.map((evidence) => evidence.line).join(', ')}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
        <CopyFixPromptButton entry={entry} includeHtml={props.includeHtml} />
      </div>
    </li>
  )
}

const COPY_LABEL = { idle: 'Copy fix prompt', copied: 'Copied', failed: 'Copy failed' } as const
const COPY_LABEL_RESET_MS = 2000

function CopyFixPromptButton(props: { entry: WidgetPublishCheck; includeHtml: boolean }) {
  const [copyState, setCopyState] = React.useState<keyof typeof COPY_LABEL>('idle')
  const reset = React.useRef<number>()

  React.useEffect(function clearResetOnUnmount() {
    return () => window.clearTimeout(reset.current)
  }, [])

  const show = (state: keyof typeof COPY_LABEL) => {
    setCopyState(state)
    window.clearTimeout(reset.current)
    reset.current = window.setTimeout(() => setCopyState('idle'), COPY_LABEL_RESET_MS)
  }

  /* navigator.clipboard is absent outside a secure context (a plain-http intranet), and then the call throws. */
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(buildFixPrompt(props.entry.widget, { includeHtml: props.includeHtml }))
      show('copied')
    } catch {
      show('failed')
    }
  }

  return (
    <Button variant="outline" size="sm" className="min-w-[8.5rem] shrink-0" onClick={copy}>
      {COPY_LABEL[copyState]}
    </Button>
  )
}

function summaryOf(check: PublishCheck, inspectorGadgetIsEnabled: boolean): string {
  if (check.progress === 'timed-out') {
    const where = check.stalledOn ? ` while reading "${check.stalledOn}"` : ''
    const look = inspectorGadgetIsEnabled ? 'open Inspector Gadget, or read that page' : 'read that page'
    return (
      `The check did not finish within ${CHECK_TIME_LIMIT_MS / 1000} seconds and was stopped${where}. A page that ` +
      `stalls the scanner is worth a look in itself: ${look}.`
    )
  }
  if (check.progress === 'failed') {
    return 'The check could not run, so nothing was read. The browser console says why; this is a fault in the check, not in a page.'
  }
  if (!check.status) return 'Checking the Code Widgets…'
  const total = check.widgets.length
  if (total === 0) return 'This app has no Code Widgets, so there is nothing for this check to read.'

  const count = (status: RiskStatus) => check.widgets.filter((entry) => entry.status === status).length
  const of = `of ${total} Code ${total === 1 ? 'Widget' : 'Widgets'}`
  if (check.status === 'flagged') return `${count('flagged')} ${of} should be fixed before publishing.`
  if (check.status === 'review') return `${count('review')} ${of} need a look before publishing.`
  return `This check found nothing in ${total} Code ${total === 1 ? 'Widget' : 'Widgets'}.`
}
